import React, { useState } from 'react';

import { numberFormat, sortByKey } from '~/core/scripts/utils';

import config, { colors } from '../config';
import { getFontColor } from '../utils';
import PieChart from './PieChart';
import CoveredCard from './CoveredCard';
import Icon from '~/core/scripts/components/Icon';
import { getIconType } from '~/kits/general/scripts/utils';

const MAX_CO2 = 300;
const c02avg = 160;
const c02target = 114;

const getCo2Type = co2 => {
  const isBelowAvg = co2 < c02avg;
  const isBelowTarget = co2 < c02target;

  if (isBelowTarget) {
    return 'belowplus';
  } else if (isBelowAvg) {
    return 'below';
  }

  return 'above';
};

const getC02Info = co2Type => {
  let label = 'viel';

  if (co2Type === 'below') {
    label = 'mittel';
  } else if (co2Type === 'belowplus') {
    label = 'wenig';
  }

  return label;
};

export default props => {
  const [isSearchMode, setSearchMode] = useState(false);

  if (!props.isUncovered) {
    return <CoveredCard {...props} isSearchMode={isSearchMode} setSearchMode={setSearchMode} />;
  }

  const segmentKeys = Object.keys(config).filter(key => key !== 'INSGESAMT');
  const segments = segmentKeys
    .filter(segment => props[segment])
    .map(segment => ({
      value: props[segment],
      ...config[segment],
      key: segment,
      sortValue: props[`${segment}_CO2`],
      hasIcon: false
    }))
    .sort(sortByKey('value'));

  if (segments && segments.length) {
    if (segments[0] && segments[0].value >= 10 && segments[0].key !== 'SONSTIGE') {
      segments[0].hasIcon = true;
    }
    if (segments[1] && segments[1].value >= 10 && segments[1].key !== 'SONSTIGE') {
      segments[1].hasIcon = true;
    }
  }

  segments.sort(sortByKey('sortValue'));

  const belowPlusHeight = (c02target / MAX_CO2) * 100;
  const belowHeight = (160 / MAX_CO2) * 100 - belowPlusHeight;
  const aboveHeight = 100 - belowHeight - belowPlusHeight;
  const currBrandCo2 = Math.min((props.CO2_Durchschnitt / MAX_CO2) * 100, 103);
  const currC02Type = getCo2Type(props.CO2_Durchschnitt);
  const currC02Info = getC02Info(currC02Type);

  return (
    <div className={`carcard__outer card-${props.index}`}>
      <div className="carcard">
        <div className="carcard__vis">
          {segments.map(segment => (
            <div
              className="carcard__vis-segment"
              style={{
                height: `${segment.value}%`,
                background: colors[+props[`${segment.key}_CO2`] - 1],
                color: getFontColor(colors[+props[`${segment.key}_CO2`] - 1])
              }}
              key={segment.key}
            >
              {segment.hasIcon ? <Icon type={getIconType(segment.key)} /> : null}
              <div className="carcard__vis-label">{segment.value > 5 ? segment.label : null}</div>
              <div className="carcard__vis-value">
                {segment.value > 15 ? `${numberFormat(segment.value, 1)} %` : null}
              </div>
            </div>
          ))}
        </div>
        <div className="carcard__info">
          <div className="carcard__info-title">{props.Marke}</div>
          <div className="carcard__co2">
            <div className="carcard__co2-title">CO₂-Ausstoß</div>
            <div className="carcard__co2-subtitle">durchschnittlich pro Auto und km</div>
            <div className="carcard__co2-viswrapper">
              <div className="carcard__co2-vis">
                <div className={`current ${currC02Type}`} style={{ bottom: `${currBrandCo2}%` }}>
                  {~~props.CO2_Durchschnitt} g
                </div>
                <div className="bar above" style={{ height: `${aboveHeight}%` }}>
                  <div className="label">
                    Deutschland <span className="diameter">⌀</span>
                  </div>
                </div>
                <div className="bar below" style={{ height: `${belowHeight}%` }}>
                  <div className="label">EU-Zielbereich</div>
                </div>
                <div className="bar belowplus" style={{ height: `${belowPlusHeight}%` }}></div>
              </div>
            </div>

            <div className={`carcard__co2-info ${currC02Type}`}>{currC02Info}</div>
          </div>

          <div className="carcard__share">
            <div className="carcard__share-title">Elektro-Anteil</div>
            <div className="carcard__share-viswrapper">
              <div className="carcard__share-vis">
                <PieChart
                  colors={['rgba(0,0,0,0.8)', 'rgba(0,0,0,0.12)']}
                  data={[{ value: props.Anteil_Elektro }, { value: 100 - props.Anteil_Elektro }]}
                  size={30}
                />
              </div>
              <div>
                <div className="carcard__share-vistitle">{numberFormat(props.Anteil_Elektro, 1)} %</div>
              </div>
            </div>
          </div>

          <div className="carcard__stats">
            <div className="carcard__stats-inner">
              <div className="carcard__stats-title">Marktanteil</div>
              <div className="carcard__stats-value">{numberFormat(props.Marktanteil, 1)} %</div>
            </div>
            <div className="carcard__stats-inner">
              <div className="carcard__stats-title">Autos gesamt</div>
              <div className="carcard__stats-value">{numberFormat(props.Anzahl_abs)}</div>
            </div>
          </div>
        </div>
      </div>
      {props.index === 0 && (
        <div className="carcard__outer-info first">
          <Icon type="arrow-left" />
          <strong>Anteil der Pkw-Typen</strong> an der Marke, eingefärbt und sortiert nach CO₂-Ausstoß (WLTP)
        </div>
      )}
      {props.index === 1 && (
        <div className="carcard__outer-info second">
          Alle Werte beziehen sich auf <strong>Neuzulassungen (2019)</strong>, Elektroanteil ohne Hybride
        </div>
      )}
    </div>
  );
};
